import { IModelDataV1, IModelDataV2, IModelDataV3, Model, ModelStatus } from '@df/catalog/model';

export const NEW_MODEL_ID = -1;
export const NEW_VERSION_MODEL_ID = -2;
export const NEW_REVISION_MODEL_ID = -3;

/**
 * Model that can be edited with iCMS
 */
export interface ICmsModel<D extends ICmsModelData = ICmsModelData> extends Model<D> {}

export interface ICmsModelData extends IModelDataV3 {
    _cms: ICmsModelCmsData;
    createdAt: number;
    updatedAt: number;
}

export enum ERelationType {
    mirror = 1,
    alternate = 2
}

export interface IRelation {
    id: string;
    parent: number;
    child: number;
    relationType: ERelationType;
    type: string;
}

/**
 * ICmsModel cms data
 */
export interface ICmsModelCmsData {
    /**
     * Model status (enabled, disaabled, ..)
     */
    status: ModelStatus;
    /**
     * Multisite data
     */
    multisite?: IMultisiteData;
    /**
     * If model is a version then this object contains metadata about it
     */
    version?: IVersionData;
}

export interface IMultisiteData {
    relations: IRelation[];
}

//////////////////////////////
//
// Versions
//
/////////////////////////////

export type TVersionType = 'revision' | 'draft';
export type TVersionStatus = 'accepted' | 'new' | 'published';

export interface IBasicVersionData {
    /**
     * Main model id (one this version is of)
     */
    originalId: number;
    /**
     * Version type
     * revision - auto saved partial data
     * draft - planned version
     */
    type: TVersionType;
    /**
     * Versions name
     */
    name: string;
    /**
     * Version identifier
     */
    versionId: number;
}

/**
 * Metadata for revision type of version
 */
export interface IRevisionVersionData extends IBasicVersionData {}

/**
 * Metadata for draft type of version
 */
export interface IDraftVersionData extends IBasicVersionData {
    /**
     * Time it is scheduled to be published
     */
    scheduledTo?: number;
    /**
     * Status
     * new, approved, applied
     */
    status: TVersionStatus;
    /**
     * Campaign id (see campaign plugin)
     */
    campaign?: number;
}

/**
 * Unviersal type for versions data
 */
export type IVersionData = IDraftVersionData | IRevisionVersionData;

export interface IPage<T extends IPageData> extends Model<T, number> {}

export interface IPageDataV1 extends IModelDataV1 {
    id: number;
    name: string;
    url: string;
    position?: number;
    parent?: number;
    groups?: number[];
    pageType?: string; // see CmsPageType decorator
}

/**
 * Verbosity 2 data
 */
export interface IPageDataV2<TypeData extends {} = any> extends IPageDataV1, IModelDataV2 {
    id: number;
    image?: string;
    updatedAt?: number;
    createdAt?: number;
    typeData?: TypeData;
}

/**
 * Verbosity 3 data
 */
export interface IPageDataV3<TypeData extends {} = any> extends IPageDataV2<TypeData>, IModelDataV3 {
    id: number;
}

/**
 * Verbosity 4 data (CMS)
 */
export interface IPageDataV4 extends IPageDataV3, ICmsModelRoutableData {
    id: number;
    description?: string;
    createdAt: number;
    updatedAt: number;
}

export type IPageData = IPageDataV1 | IPageDataV2 | IPageDataV3 | IPageDataV4;

/**
 * Routable model that can be edited with iCMS
 */
export interface ICmsModelRoutable {
    data: ICmsModelRoutableData;
}

export interface ICmsModelRoutableData extends ICmsModelData {
    _cms: ICmsModelRoutableCmsData;
}

/**
 * ICmsModelRoutable cms data
 */
export interface ICmsModelRoutableCmsData extends ICmsModelCmsData {
    /**
     * Absolute (starting from slash) or relative to another page
     */
    absoluteUrl: boolean;
    /**
     * URL slug
     * we keep it separately from URL for relative urls, cause it then depends on parent page URL
     */
    slug?: string;
    /**
     * Model URL
     */
    url?: string;
    /**
     * Alternates - pages on different sites that are presented as alternates
     */
    alternates?: { [storeId: number]: number | string };
}

export interface IMediaData extends IModelDataV1 {
    path: string;
    name: string;
    // alt & title for image HTML element
    alt?: string;
    title?: string;
    imageTags?: number[];
    createdAt: number;
    fileMeta?: IFileMeta;
    _cms?: {
        createdBy?: string;
    };
}

export type TMediaType = 'audio' | 'image' | 'document';

export interface IFileMeta {
    size: number;
    type: string;
    typeGroup: string;
    width?: number;
    height?: number;
}

export interface IGroupData extends IModelDataV1 {
    id: number;
    name: string;
}

export interface IImageTagData extends IModelDataV1 {
    id: number;
    name: string;
    position: number;
}

export interface ICmsApiUploadRequest {
    file: File;
    type: 'media';
    data?: Partial<IMediaData>;
}

/**
 * Save action result data format
 */
export interface ISaveResult {
    result: { id: number };
}

/**
 * Uploadr response data
 */
export interface ICmsApiUploadResponse extends ISaveResult {}

export interface IDraftData<TTargetData> extends IModelDataV1 {
    target: number;
    targetType: string;
    draftData: TTargetData;
    status: TDraftStatus;
    name: string;
    scheduledTo?: number;
    revertAt?: number;
    createdAt: number;
    publishedAt?: number;
    campaign?: number;
    hash: string;
}

export type TDraftStatus = 'new' | 'accepted' | 'published' | undefined;
